/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@store/store";
import {
  setDocumentsAnexo1ConyugeSecondGuarantee,
  setDocumentsAnexo1SecondGuarantee,
  setDocumentsConyugeSecondGuarantee,
  setDocumentsLoading,
  setDocumentsSecondGuarantee,
} from "@store/slices/segundoAvalSlice";
import { BofuTabDocument } from "@interfaces/response";
import { useLazyGetTabsConfigQuery } from "@api/catalogs";
import { UUID } from "crypto";
import {
  findDocumentValue,
  generateDocumentConfig,
  generatePropietarioDocs,
  validateDocumentStructure,
} from "./functions";
import * as Sentry from "@sentry/react";
import {
  useLazyGetDocumentAppIdSecondGuaranteeQuery,
  useLazyGetPersonByIdGuaranteeQuery,
  useLazyGetPersonsSecondGuaranteeQuery,
} from "@api/second_guarantee_api/secondGuaranteeApi";
import { DOCUMENTS_CATALOG } from "@helpers/constantes";

interface Props {
  isMarriageAval?: boolean;
}

const SegundoAvalDocuments = ({ isMarriageAval }: Props) => {
  const dispatch = useDispatch();

  const shareholder = useSelector((state: RootState) => state.secondGuarante.guarantee);
  const applicationData = useSelector((state: RootState) => state.register.application);

  const [getTabsConfig] = useLazyGetTabsConfigQuery();
  const [getDocsByAppId] = useLazyGetDocumentAppIdSecondGuaranteeQuery();
  const [getPersons] = useLazyGetPersonsSecondGuaranteeQuery();
  const [getPerson] = useLazyGetPersonByIdGuaranteeQuery();

  const generateTabsStructure = async () => {
    try {
      dispatch(setDocumentsLoading(true));

      const [tabs_config, persons, documents] = await Promise.all([
        getTabsConfig({ taxcode: String(applicationData.tax_systems?.code) }).unwrap(),
        getPersons({
          loanApplicationId: shareholder.loan_application_id as UUID,
          id: shareholder.id as UUID,
        }).unwrap(),
        getDocsByAppId({
          loanApplicationId: shareholder.loan_application_id as UUID,
          status: true,
        }).unwrap(),
      ]);

      if (tabs_config.length === 0) {
        dispatch(setDocumentsLoading(false));
        return;
      }

      const segundo_aval = persons[0];
      if (!segundo_aval) {
        dispatch(setDocumentsLoading(false));
        return;
      }

      const { anexo_1 } = DOCUMENTS_CATALOG;

      const valid_tab_documents: BofuTabDocument[] = [];

      // filtrar los documentos validos para aval y conyuge de aval
      tabs_config.forEach((tab) => {
        tab.documents
          .filter((item) => validateDocumentStructure(item))
          .forEach((docItem) => {
            const document_value = generateDocumentConfig({
              docItem,
              docs_account: [],
              docs_aval: [],
              clientDetailsId: shareholder.client_details_id as UUID,
              aval: segundo_aval,
            });

            valid_tab_documents.push(document_value);
          });
      });

      // identificar los documentos para cada tipo de persona
      const tab_documents_aval = valid_tab_documents.filter((item) => item.relationship === "aval");
      const tab_documents_conyuge_aval = valid_tab_documents.filter((item) => item.relationship === "conyuge_aval");

      // generar los documentos con valor para el segundo aval
      const docs_with_value_aval = generatePropietarioDocs({
        personItem: segundo_aval,
        valid_documents: tab_documents_aval,
        documents_uploaded: documents,
        clientDetailsId: shareholder.client_details_id as UUID,
      });

      // obtener el anexo 1 del segundo aval
      const doc_anexo_1 = findDocumentValue({
        documents,
        file_code: anexo_1.code,
        person_id: segundo_aval?.id,
      });

      // establecer los documentos en el estado
      dispatch(setDocumentsAnexo1SecondGuarantee(doc_anexo_1));
      dispatch(setDocumentsSecondGuarantee(docs_with_value_aval));

      if (isMarriageAval) {
        // obtener el conyuge del segundo aval
        const conyuge_aval = await getPerson(String(segundo_aval.spouse_id)).unwrap();

        // generar los documentos con valor para el conyuge del segundo aval
        const docs_with_value_conyuge_aval = generatePropietarioDocs({
          personItem: conyuge_aval,
          valid_documents: tab_documents_conyuge_aval,
          documents_uploaded: documents,
          clientDetailsId: shareholder.client_details_id as UUID,
        });

        // obtener el anexo 1 del conyuge del segundo aval
        const doc_anexo_1_conyuge = findDocumentValue({
          documents,
          file_code: anexo_1.code,
          person_id: conyuge_aval?.id,
        });

        dispatch(setDocumentsAnexo1ConyugeSecondGuarantee(doc_anexo_1_conyuge));
        dispatch(setDocumentsConyugeSecondGuarantee(docs_with_value_conyuge_aval));
      }
    } catch (error) {
      console.error("Ha ocurrido un error al generar los documentos", error);
      Sentry.captureException(error, {
        extra: {
          component: "SegundoAval-generateTabsStructure",
        },
      });
    } finally {
      dispatch(setDocumentsLoading(false));
    }
  };

  useEffect(() => {
    generateTabsStructure();
  }, [isMarriageAval]);

  return null;
};

export default SegundoAvalDocuments;
